import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  inject,
} from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';

import { AlertActionType, AlertSeverity } from './types';

/**
 * Alert Component
 */
@Component({
  selector: 'mimecast-ui-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  standalone: false,
  host: {
    '[attr.role]': "severity === 'error' ? 'alert' : 'status'",
    '[attr.tabindex]': "severity === 'error' ? -1 : null",
  },
})
export class AlertComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);

  // Using "title" as an Input name renders native `title` element on top of the component.
  /**
   * Title of the alert
   * @type {string | null}
   */
  @Input()
  titleText?: string | null;
  /**
   * Alert message to display to the user
   * @default ''
   */
  @Input()
  message: string | TemplateRef<ElementRef> = '';
  /**
   * Severity of the alert: `'info' | 'warn' | 'success' | 'error'`.
   * (`'evaluated'` is not an alert severity; use the evaluated chip/theme elsewhere.)
   * @type {AlertSeverity}
   * @default 'info'
   */
  @Input()
  severity: AlertSeverity = 'info';
  /**
   * The type of action of the alert. It can be either 'action', 'close' or null.
   * @type {AlertActionType}
   * @default null
   */
  @Input()
  actionType?: AlertActionType = null;
  /**
   * Text of the action button
   * @type {string}
   */
  @Input()
  actionBtnText?: string;
  /**
   * Alert action event
   */
  @Output()
  action = new EventEmitter<void>();
  /**
   * Closed action event
   */
  @Output()
  closed = new EventEmitter<void>();

  /**
   * Whether the alert has a coloured background (default is true)
   */
  @Input()
  colouredBackground = true;
  /**
   * Whether the alert has an icon (default is true)
   */
  @Input()
  showIcon = true;
  /**
   * Types of icons used to display its severity
   * @type {Record<AlertSeverity, IconProp>}
   */
  severityIcons: Record<AlertSeverity, IconProp> = {
    error: faCircleExclamation as IconProp,
    warn: faTriangleExclamation as IconProp,
    info: faCircleInfo as IconProp,
    success: faCircleCheck as IconProp,
  };
  /**
   * Aria accessibility labels for Dismiss button
   */
  readonly dismissAria: Record<AlertSeverity, string> = {
    error: $localize`Dismiss error`,
    warn: $localize`Dismiss warning`,
    info: $localize`Dismiss message`,
    success: $localize`Dismiss success`,
  };
  /**
   * Aria accessibility labels for Alert type icon
   */
  readonly severityIconAria: Record<AlertSeverity, string> = {
    error: $localize`Error notification`,
    warn: $localize`Warning notification`,
    info: $localize`Message`,
    success: $localize`Success`,
  };
  /**
   * Type of icon used for close action
   * @default faXmark as IconProp
   */
  closeIcon = faXmark as IconProp;

  /**
   * Moves focus to the alert host (error severity uses tabindex=-1).
   */
  focus(): void {
    this.elementRef.nativeElement.focus();
  }

  isTemplateRef(value: unknown): value is TemplateRef<ElementRef> {
    return value instanceof TemplateRef;
  }
}
